import { ClickAwayListener } from '@mui/material';
import { DotSpinner } from '@crono-one/design-system';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { ReactComponent as ReloadIcon } from 'crono-fe-common/icons/Reload.svg';
import usePatchUserPreferences from 'hooks/services/user/usePatchUserPreferences';
import { useAuth } from 'context/auth';
import IconEmptyStrategies from 'crono-fe-common/icons/Icon-Empty-Strategies';
import { List, ListType } from 'crono-fe-common/types/list';
import ListModal from '../ListModal';
import ListOptionRow from '../ListOptionRow';
import ListPickerShell from '../ListPickerShell';
import { useAnalytics } from 'context/analytics';
import useSearchListsInfinite from 'hooks/services/lists/useSearchListInfinite';
import { Prospect } from 'crono-fe-common/types/prospect';
import { Account } from 'crono-fe-common/types/account';

interface IProps {
  listType: ListType;
  onClose: () => void;
  //Function called when a list is opened from the search
  onOpenList?: (listId: number) => void;
  //Shows the copy-link and edit actions on the rows; edit calls this
  onEditList?: (list: List) => void;
  handleProspectAccountClick?: (list: List) => void;
  currentListId?: number;
  forDropdown?: boolean;
  onChooseList?: (list: List | null) => void;
  addToListDirectly?: boolean;
  selectedEntities?: (Prospect | Account)[];
  maxResultsHeight?: string;
  wrapperHeight?: string;
  showAllOption?: boolean;
  defaultNewListName?: string;
}

const LIMIT_OF_LISTS = 10;
export const LIMIT_OF_CREATED_LISTS = 100;

const RowDivider = () => <div className="h-px bg-neutral-300" />;

const ListSearch = ({
  listType,
  onClose,
  onOpenList,
  onEditList,
  handleProspectAccountClick,
  currentListId,
  forDropdown,
  onChooseList,
  addToListDirectly = false,
  selectedEntities = [],
  maxResultsHeight,
  wrapperHeight,
  showAllOption,
  defaultNewListName,
}: IProps) => {
  const { user } = useAuth();
  const { mutateAsync: patchUserPreferences } = usePatchUserPreferences();
  const [search, setSearch] = useState<string>('');
  const [onlyMine, setOnlyMine] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);

  const {
    data: listsData,
    isLoading: listsSearchLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useSearchListsInfinite({
    limit: LIMIT_OF_LISTS,
    offset: 0,
    name: search,
    type: listType,
    sortType: 'CreationDesc',
    owned: onlyMine || undefined,
  });

  const lists: List[] = useMemo(() => {
    if (!listsData) return [];
    return listsData.pages.flatMap((listsPage) => {
      return listsPage.data.data;
    });
  }, [listsData]);

  const total: number = useMemo(() => {
    if (!listsData) return 0;
    return listsData.pages.at(0)?.data.totalToConsider || 0;
  }, [listsData]);

  const userReachedMaxLists: boolean = total >= LIMIT_OF_CREATED_LISTS;

  const analytics = useAnalytics();

  const handleClickOpenList = useCallback(
    async (listId: number) => {
      const currentOpenLists: number[] =
        user?.userPreferences.openListsIds || [];

      const filteredOpenLists: number[] = currentOpenLists.filter(
        (id) => id !== listId,
      );

      //we want to have the opened list at the beggining of the lists
      const newOpenLists: number[] = [listId, ...filteredOpenLists];

      await patchUserPreferences({
        openListsIds: newOpenLists,
      });

      if (user) {
        user.userPreferences.openListsIds = newOpenLists;
      }
      onOpenList?.(listId);
      if (analytics) {
        analytics.track('show-list', { listId });
      }

      onClose();
    },
    [user, patchUserPreferences],
  );

  const handleAddDirectlyToDropdown = useCallback(
    (list: List) => {
      if (forDropdown && onChooseList) {
        onChooseList(list);
        onClose();
      }
    },
    [forDropdown, onChooseList],
  );

  const isListDisabled = (list: List) => {
    //we are already inside that list
    if (currentListId === list.id) return true;
    //all entities are already present in this list
    return (
      selectedEntities.length > 0 &&
      selectedEntities.every((entity) =>
        entity.cronoLists?.some((cronoList) => cronoList.id === list.id),
      )
    );
  };

  const handleSelectList = (list: List) => {
    if (forDropdown && onChooseList) {
      onChooseList(list);
      onClose();
      return;
    }

    handleClickOpenList(list.id);

    if (handleProspectAccountClick) {
      handleProspectAccountClick(list);
    }
  };

  return (
    <>
      {openCreateModal && (
        <ListModal
          type={listType}
          onClose={() => {
            setOpenCreateModal(false);
          }}
          precompletedName={search || defaultNewListName}
          addToListDirectly={addToListDirectly}
          handleProspectAccountClick={handleProspectAccountClick}
          handleAddDirectlyToDropdown={handleAddDirectlyToDropdown}
          onCreate={(listId: number) => {
            onOpenList?.(listId);
            onClose();
          }}
        />
      )}

      {/* The create-list dialog is a sibling of this listener, not a
          descendant, so clicks inside it read as clicks away. */}
      <ClickAwayListener
        onClickAway={() => {
          if (openCreateModal) return;
          onClose();
        }}
      >
        <div className={forDropdown ? 'w-full' : undefined}>
          <ListPickerShell
            search={search}
            onSearchChange={setSearch}
            onlyMine={onlyMine}
            onOnlyMineChange={setOnlyMine}
            onNew={() => setOpenCreateModal(true)}
            newDisabledReason={
              userReachedMaxLists
                ? `You reached limit of ${LIMIT_OF_CREATED_LISTS} max lists`
                : null
            }
            className={forDropdown ? 'w-full' : undefined}
            style={wrapperHeight ? { height: wrapperHeight } : undefined}
            bodyStyle={
              maxResultsHeight ? { maxHeight: maxResultsHeight } : undefined
            }
          >
            {listsSearchLoading && !lists.length && (
              <div className="flex justify-center py-6">
                <DotSpinner />
              </div>
            )}

            {!lists.length && !listsSearchLoading && (
              <div className="flex flex-col items-center gap-1 px-4 py-10 text-center">
                <div className="mb-3">
                  <IconEmptyStrategies />
                </div>
                <span className="text-sm font-semibold">
                  No lists {search ? 'found with this search' : ''}
                </span>
                <span className="max-w-58 text-xs text-gray-700">
                  Create a custom list to group your contacts and companies.
                </span>
              </div>
            )}

            {lists.length > 0 && (
              <>
                {showAllOption && (
                  <>
                    <button
                      type="button"
                      className="flex min-h-10 w-full cursor-pointer items-center rounded-lg border-0 bg-transparent p-2 text-left text-xs font-medium leading-4 hover:bg-gray-100"
                      onClick={() => {
                        if (onChooseList) {
                          onChooseList(null);
                          onClose();
                        }
                      }}
                    >
                      All lists
                    </button>
                    <RowDivider />
                  </>
                )}

                {lists.map((list, index) => (
                  <Fragment key={list.id}>
                    <ListOptionRow
                      list={list}
                      disabled={isListDisabled(list)}
                      onSelect={() => handleSelectList(list)}
                      withActions={!forDropdown && !!onEditList}
                      onEdit={(listToEdit) => {
                        onEditList?.(listToEdit);
                        onClose();
                      }}
                    />
                    {index < lists.length - 1 && <RowDivider />}
                  </Fragment>
                ))}

                {isFetchingNextPage ? (
                  <div className="flex justify-center py-4">
                    <DotSpinner />
                  </div>
                ) : (
                  hasNextPage && (
                    <button
                      type="button"
                      className="mx-auto mt-2 flex cursor-pointer items-center gap-2 border-0 bg-transparent px-0 py-1 text-sm font-medium text-primary-800"
                      onClick={() => fetchNextPage()}
                    >
                      <ReloadIcon />
                      Load more
                    </button>
                  )
                )}
              </>
            )}
          </ListPickerShell>
        </div>
      </ClickAwayListener>
    </>
  );
};

export default ListSearch;
